import { useMemo, useState } from "react";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { Link } from "react-router-dom";
import classNames from "classnames";
import { Empty } from "antd";
import { useCompareSelection } from "Explore/Context/CompareSelectionContext";
import styles from "./CompareItemNav.module.scss";

const SECTION_ORDER = [
  { type: "faculty", title: "Researchers / faculty" },
  { type: "institution", title: "Institutions" },
  { type: "unit", title: "Units" },
  { type: "venue", title: "Venues" },
];

const CompareItemNav = ({ whiteTheme }) => {
  const { items, remove } = useCompareSelection();
  const [open, setOpen] = useState(false);

  const grouped = useMemo(() => {
    const map = {
      faculty: [],
      institution: [],
      unit: [],
      venue: [],
    };
    items.forEach((item) => {
      if (map[item.compareType]) map[item.compareType].push(item);
    });
    return map;
  }, [items]);

  return (
    <>
      <div
        className={classNames(styles.trigger, {
          [styles["white-theme"]]: whiteTheme,
        })}
        onClick={() => setOpen(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-label="Open compare list"
      >
        <CompareArrowsIcon sx={{ fontSize: 26 }} />
        {items.length > 0 ? (
          <span className={styles.badge}>{items.length}</span>
        ) : null}
      </div>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <div className={styles.drawer}>
          <div className={styles["drawer__header"]}>
            <h1 className={styles["drawer__header--title"]}>Compare</h1>
            <IconButton onClick={() => setOpen(false)} aria-label="Close">
              <CloseIcon />
            </IconButton>
          </div>

          <div className={styles["drawer__actions"]}>
            <Link
              to="/explore/keyword-benchmarking"
              className={styles["drawer__benchmark-link"]}
              onClick={() => setOpen(false)}
            >
              <OpenInNewIcon sx={{ fontSize: 18 }} />
              Open research topics benchmark
            </Link>
          </div>

          <div className={styles["drawer__content"]}>
            {items.length === 0 ? (
              <Empty description="Add faculty, institutions, units, or venues from list pages. Items expire after one hour." />
            ) : (
              SECTION_ORDER.map(({ type, title }) => {
                const list = grouped[type];
                if (!list.length) return null;
                return (
                  <section key={type} className={styles["drawer__section"]}>
                    <h2 className={styles["drawer__section-title"]}>{title}</h2>
                    {list.map((item) => (
                      <div key={`${type}:${item.id}`} className={styles["drawer__row"]}>
                        <span className={styles["drawer__row-label"]} title={item.label}>
                          {item.label}
                        </span>
                        <IconButton
                          size="small"
                          aria-label={`Remove ${item.label} from compare`}
                          onClick={() => remove(type, item.id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </div>
                    ))}
                  </section>
                );
              })
            )}
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default CompareItemNav;
